import React, { useCallback, useRef, useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import { Controller, useWatch, useFormContext } from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { NumericFormat } from "react-number-format";
import {
  IAddressModel,
  ILookupItemModel,
  ILookupItemSimpleModel,
  ITransactionTypeModel,
  ReferrerTypeEnumModel,
} from "../../types/types";
import SingleValueAutoCompleteCategory from "../../ui-components/SingleValueAutoCompleteCategory";
import MultiValueAutoCompleteCategory from "../../ui-components/MultiValueAutoCompleteCategory";

import AddressInput from "../GoogleLocation/AddressInput";
import InputAdornment from "@mui/material/InputAdornment";
import { RootState } from "../../store/reducers";
import { useSelector } from "react-redux";
import ClientAutoComplete from "../AutoComplete/ClientAutoComplete";
import ReferrarAutoComplete from "../AutoComplete/ReferrarAutoComplete";
import {
  TRANSACTION_STATUS_OPTIONS,
  TransactionStatus,
  closingDateLabel,
  isContractedStatus,
  isLeadOrActive,
  isPipelineStatus,
  transactionStatusLabel,
} from "../../hooks/usePipeline";

/**
 * Extra required fields of a Lead/Active entry by Transaction Type. Only the system types
 * qualify: a type the user added in Settings needs the base fields only.
 */
const leadTypeRequirements = (type?: ITransactionTypeModel) => {
  const name = type?.system ? type.name?.trim().toLowerCase() : undefined;
  return {
    address: name === "seller" || name === "landlord",
    clients:
      name === "buyer" ||
      name === "tenant" ||
      (!!type?.system && !!type.showReferralOut),
  };
};

declare global {
  interface Window {
    initMap?: () => void;
  }
}

interface IProps {
  year: number;
  readonly?: boolean;
  isCreate?: boolean;
  /** Creating from the Pipeline folder (no folder year to stay within) */
  isPipelineFolder?: boolean;
}

export default function BasicTransaction(props: IProps) {
  const {
    year,
    readonly = false,
    isCreate = false,
    isPipelineFolder = false,
  } = props;
  const {
    control,
    setValue,
    getValues,
    trigger,
    getFieldState,
    formState,
  } = useFormContext();
  const closingDate = useWatch({ control, name: "date" });

  const status = (useWatch({ control, name: "status" }) ??
    TransactionStatus.Transaction) as TransactionStatus;
  const isPipelineEntry = isPipelineStatus(status);
  // Validators read refs so they always see the current Status
  const statusRef = useRef(status);
  statusRef.current = status;
  // New Closed entries created inside a year folder must close in that year
  const restrictToYear = isCreate && !isPipelineFolder && !isPipelineEntry;
  const restrictToYearRef = useRef(restrictToYear);
  restrictToYearRef.current = restrictToYear;
  // A Closed entry can't close in the future (Under Contract covers that)
  const noFutureClosing = status === TransactionStatus.Transaction;
  const initialStatus = formState.defaultValues?.status as
    | TransactionStatus
    | undefined;
  const [pendingStatus, setPendingStatus] = useState<TransactionStatus | null>(
    null,
  );

  const applyStatus = (newStatus: TransactionStatus) => {
    setValue("status", newStatus, { shouldDirty: true });
    // The validators below run before the next render, so move the refs now
    statusRef.current = newStatus;
    restrictToYearRef.current =
      isCreate && !isPipelineFolder && !isPipelineStatus(newStatus);
    const leadReceivedDate = getValues("leadReceivedDate");
    if (isLeadOrActive(newStatus)) {
      if (!leadReceivedDate) {
        setValue("leadReceivedDate", dayjs().startOf("day"), {
          shouldDirty: true,
        });
      }
    } else if (
      isCreate &&
      leadReceivedDate &&
      dayjs(leadReceivedDate).isSame(dayjs(), "day")
    ) {
      // Untouched default: a new Under Contract/Closed entry starts with a blank Lead Received Date
      setValue("leadReceivedDate", null, { shouldDirty: true });
    }
    // Required fields and messages depend on the Status; refresh the errors shown
    (["leadReceivedDate", "address", "date", "clients"] as const).forEach(
      (name) => {
        if (getFieldState(name).error) trigger(name);
      },
    );
    // Show right away that a Closed entry can't keep a future date
    if (
      newStatus === TransactionStatus.Transaction &&
      getValues("date")
    ) {
      trigger("date");
    }
  };

  const handleStatusChange = (newStatus: TransactionStatus) => {
    // A saved entry crossing between the Pipeline folder (Lead/Active/Under Contract) and a
    // year folder (Closed) changes where it counts: confirm first
    if (
      !isCreate &&
      initialStatus !== undefined &&
      isPipelineEntry === isPipelineStatus(initialStatus) &&
      isPipelineStatus(newStatus) !== isPipelineStatus(initialStatus)
    ) {
      setPendingStatus(newStatus);
      return;
    }
    applyStatus(newStatus);
  };
  const isMovingToTransactions =
    pendingStatus === TransactionStatus.Transaction;

  const { data: userData } = useSelector(
    (state: RootState) => state.commonData.userData,
  );

  const { hiddenFields } = useSelector(
    (state: RootState) => state.hiddenFields,
  );
  const selectedSource = useWatch({
    control,
    name: "source",
  }) as number | undefined;

  const selectedType = useWatch({
    control,
    name: "transactionType",
  }) as number | undefined;

  const selectedReason = useWatch({
    control,
    name: "reason",
  }) as number | undefined;

  const isDifferentYear = (date: Dayjs) => date.get("year") !== year;
  const isMonthlyRent =
    (selectedType &&
      userData?.transactionTypes?.find((x) => x.id === selectedType)
        ?.isMonthlyRent) ??
    false;

  const showReferralOut =
    (selectedType &&
      userData?.transactionTypes?.find((x) => x.id === selectedType)
        ?.showReferralOut) ??
    false;

  // Validators read the type from the form, so they never see a stale selection
  const transactionTypesRef = useRef(userData?.transactionTypes);
  transactionTypesRef.current = userData?.transactionTypes;
  const currentLeadRequirements = () =>
    leadTypeRequirements(
      transactionTypesRef.current?.find(
        (x) => x.id === getValues("transactionType"),
      ),
    );
  const leadRequirements = leadTypeRequirements(
    userData?.transactionTypes?.find((x) => x.id === selectedType),
  );
  const addressRequired =
    isContractedStatus(status) ||
    (isLeadOrActive(status) && leadRequirements.address);
  const clientsRequired = isLeadOrActive(status) && leadRequirements.clients;

  const isGoogleModeRef = useRef(true); // Track mode synchronously

  // Update ref when mode changes
  const handleModeChange = useCallback((newMode: boolean) => {
    isGoogleModeRef.current = newMode;
  }, []);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Controller
          name="status"
          control={control}
          defaultValue={TransactionStatus.Transaction}
          rules={{ required: "Please select a status" }}
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel shrink={true}>
                Status <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <Select
                label="Status *"
                notched={true}
                readOnly={readonly}
                value={field.value ?? TransactionStatus.Transaction}
                onBlur={field.onBlur}
                onChange={(e) =>
                  handleStatusChange(e.target.value as TransactionStatus)
                }
                renderValue={(value) =>
                  transactionStatusLabel(value as TransactionStatus)
                }
              >
                {TRANSACTION_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                    {option.note && (
                      <Box
                        component="span"
                        sx={{ color: "text.secondary", ml: 0.5 }}
                      >
                        ({option.note})
                      </Box>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Controller
          name="leadReceivedDate"
          control={control}
          defaultValue={null}
          disabled={readonly}
          rules={{
            validate: (value) => {
              if (!value) {
                return isLeadOrActive(statusRef.current)
                  ? "Please enter the Lead Received Date"
                  : true;
              }
              if (dayjs(value).isAfter(dayjs(), "day")) {
                return "Lead Received Date can't be later than today";
              }
              const closingDate = getValues("date");
              if (
                closingDate &&
                dayjs(value).isAfter(dayjs(closingDate), "day")
              ) {
                return `Lead Received Date can't be after the ${closingDateLabel(statusRef.current)}`;
              }
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              <DatePicker
                disabled={readonly}
                {...field}
                label={
                  <span>
                    Lead Received Date
                    {isLeadOrActive(status) && (
                      <span style={{ color: "red" }}> *</span>
                    )}
                  </span>
                }
                disableFuture
                value={field.value ? dayjs(field.value) : null}
                onChange={(newValue) => field.onChange(newValue)}
                slotProps={{
                  textField: {
                    onBlur: field.onBlur,
                    fullWidth: true,
                    error: !!error,
                    InputLabelProps: {
                      shrink: true,
                      sx: { backgroundColor: "background.paper", px: 1 },
                    },
                  },
                  field: { onBlur: () => field.onBlur(), clearable: true },
                  popper: { disablePortal: true },
                }}
              />
              {error && (
                <Typography variant="caption" color="error">
                  {error.message}
                </Typography>
              )}
            </FormControl>
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Controller
          name="transactionType"
          rules={{ required: "Please select a transaction type" }}
          control={control}
          defaultValue={""}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              <InputLabel shrink={true}>
                Transaction Type <span style={{ color: "red" }}>*</span>
              </InputLabel>

              <Select
                {...field}
                label="Transaction Type *"
                // aria-invalid={control.name ? "true" : "false"}
                readOnly={readonly}
                notched={true}
                value={field.value ?? ""}
                onBlur={field.onBlur}
                displayEmpty={true}
                onChange={(e) => {
                  // debugger;
                  // const selected = transactionTypes.find(
                  //   (opt) => opt.id === e.target.value
                  // );
                  field.onChange(e.target.value ?? null);
                  // Lead/Active required fields depend on the type
                  (["address", "clients"] as const).forEach((name) => {
                    if (getFieldState(name).error) trigger(name);
                  });
                }}
              >
                {userData?.transactionTypes
                  ?.filter(
                    (x) =>
                      (isCreate && x.deleted !== true) ||
                      (isCreate === false &&
                        (selectedType === x.id || x.deleted !== true)),
                  )
                  .map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
              </Select>
              {error && (
                <Typography variant="caption" color="error">
                  Please select a transaction type
                </Typography>
              )}
            </FormControl>
          )}
        />
      </Grid>

      {/* {!hiddenFields.find((x) => x.name === "address") && ( */}
      <Grid item xs={12} md={6}>
        <Controller
          name="address"
          control={control}
          defaultValue={null}
          rules={{
            validate: (value: IAddressModel) => {
              if (
                !value ||
                !value.formattedAddress ||
                !value.formattedAddress.trim()
              ) {
                // Required for Under Contract/Closed, and for Lead/Active Seller/Landlord entries
                const required =
                  isContractedStatus(statusRef.current) ||
                  (isLeadOrActive(statusRef.current) &&
                    currentLeadRequirements().address);
                return required ? "Please enter an address" : true;
              }

              // Use ref instead of state for immediate access
              const currentIsGoogleMode = isGoogleModeRef.current;

              // Check if it's a valid Google address by checking for coordinates and structured data
              const hasCoordinates =
                typeof value.latitude === "number" &&
                typeof value.longitude === "number";
              const hasStructuredData =
                (value.street && value.street.trim() !== "") ||
                (value.state && value.state.trim() !== "") ||
                (value.zip && value.zip.trim() !== "");

              // Valid scenarios:
              // 1. Full Google address (has both coordinates AND structured data)
              // 2. Free text (has formatted address but NO coordinates and NO structured data)
              const isValid = currentIsGoogleMode
                ? hasCoordinates && hasStructuredData
                : value?.formattedAddress?.trim().length > 1;

              if (isValid) {
                return true;
              }

              return currentIsGoogleMode
                ? "Please select a valid Google address from the suggestions"
                : "Please enter a valid address";
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              <InputLabel shrink={true}>
                Address{" "}
                {addressRequired && <span style={{ color: "red" }}>*</span>}
              </InputLabel>
              <AddressInput
                value={field.value}
                onModeChange={handleModeChange}
                onChange={(value) => {
                  field.onChange(value);
                }}
              />
              {error && (
                <Typography variant="caption" color="error">
                  {error.message}
                </Typography>
              )}
            </FormControl>
          )}
        />
      </Grid>
      {/* )} */}
      <Grid item xs={12} md={6}>
        <Controller
          name="date"
          disabled={readonly}
          defaultValue={null}
          rules={{
            required: "Please select a date",
            // Re-check the Lead Received Date whenever the closing date changes
            deps: ["leadReceivedDate"],
            validate: (value) => {
              if (!value) return true;
              if (
                statusRef.current === TransactionStatus.Transaction &&
                dayjs(value).isAfter(dayjs(), "day")
              ) {
                return "Closing Date can't be in the future. Use the Under Contract status for deals that haven't closed yet";
              }
              if (!restrictToYearRef.current) return true;
              const dateYear = dayjs(value).year();
              return dateYear === year || `Date must be in ${year}`;
            },
          }}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              {/* <InputLabel shrink={true}>Date</InputLabel> */}
              <DatePicker
                disabled={readonly}
                shouldDisableYear={restrictToYear ? isDifferentYear : undefined}
                minDate={
                  restrictToYear ? dayjs(new Date(year, 0, 1)) : undefined
                }
                maxDate={
                  restrictToYear
                    ? dayjs(new Date(year + 1, 0, 1)).add(-1, "day")
                    : undefined
                }
                disableFuture={noFutureClosing}
                // openTo="year"
                // defaultMonth={dayjs(new Date(year, 0, 1))}
                {...field}
                label={
                  <span>
                    {closingDateLabel(status)}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </span>
                }
                value={field.value ? dayjs(field.value) : null}
                onChange={(newValue) => field.onChange(newValue)}
                slotProps={{
                  textField: {
                    onBlur: field.onBlur,
                    fullWidth: true,
                    error: !!error,
                    // helperText: error?.message,
                    InputLabelProps: {
                      shrink: true,
                      sx: {
                        backgroundColor: "background.paper",
                        px: 1,
                        // "& .MuiOutlinedInput-notchedOutline": {
                        //   border: "none",
                        // },
                      },
                    },
                  },

                  field: { onBlur: () => field.onBlur(), clearable: true },
                  popper: {
                    // onClose: () => field.onBlur(),
                    disablePortal: true,
                  },
                }}
              />
              {error && error.type === "required" && (
                <Typography variant="caption" color="error">
                  Please select a date
                </Typography>
              )}
              {error && error.type === "validate" && (
                <Typography variant="caption" color="error">
                  {error.message}
                </Typography>
              )}
            </FormControl>
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Controller
          name="price"
          control={control}
          rules={{
            required: `Please enter a ${
              isMonthlyRent ? "monthly Rent" : "price"
            }`,
          }}
          // defaultValue={null}
          defaultValue=""
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth variant="filled">
              <NumericFormat
                fullWidth
                valueIsNumericString
                id="price"
                customInput={TextField}
                thousandSeparator
                onValueChange={(values) => field.onChange(values.floatValue)}
                allowLeadingZeros={false}
                decimalScale={0}
                onBlur={field.onBlur}
                // onChange={field.onChange}
                value={field.value ?? ""}
                label={
                  <span>
                    {isMonthlyRent ? "Monthly Rent" : "Price"}
                    <span style={{ color: "red" }}>*</span>
                  </span>
                }
                allowNegative={false}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
                inputProps={{
                  maxLength: 50,
                }}
              />
              {/* <TextField
                disabled={readonly}
                id="price"
                type="number"
                error={!!error}
                // helperText={error?.message}
                label={
                  <span>
                    {isMonthlyRent ? "Monthly Rent" : "Price"}
                    <span style={{ color: "red" }}>*</span>
                  </span>
                }
                fullWidth
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value ?? ""}
                InputProps={{
                  inputComponent: NumericFormat as any,
                  inputProps: {
                    onBlur: field.onBlur,
                    onChange: field.onChange,
                    value: field.value ?? "",
                    displayType: "input",
                    thousandSeparator: ",",
                    decimalScale: 0,
                    prefix: "$",
                    allowNegative: false,
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
              /> */}
              {error && (
                <Typography variant="caption" color="error">
                  Please enter a {isMonthlyRent ? "monthly Rent" : "price"}
                </Typography>
              )}
            </FormControl>
          )}
        />
      </Grid>

      {!hiddenFields.find((x) => x.name === "source") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="source"
            control={control}
            defaultValue={""}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel shrink={true}>Source</InputLabel>
                <Select
                  notched={true}
                  readOnly={readonly}
                  displayEmpty
                  {...field}
                  label="Source"
                  value={field.value ?? ""}
                  fullWidth
                  onChange={(e) => {
                    field.onChange(e.target.value ?? null);
                  }}
                  endAdornment={
                    field.value &&
                    !readonly && (
                      <IconButton
                        size="small"
                        sx={{ mr: 2 }}
                        onClick={(e) => {
                          field.onChange(null);
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    )
                  }
                >
                  {userData?.sources
                    ?.filter((x) => x.name !== undefined || x.name !== "")
                    ?.sort((a, b) => a.name!.localeCompare(b.name!))
                    ?.filter(
                      (x) =>
                        (isCreate && x.deleted !== true) ||
                        (isCreate === false &&
                          (selectedSource === x.id || x.deleted !== true)),
                    )
                    ?.map((source) => (
                      <MenuItem key={source.id} value={source.id}>
                        {source.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
      )}
      {selectedSource &&
        !hiddenFields.find((x) => x.name === "source") &&
        userData?.sources?.find((x) => x.id === selectedSource)
          ?.showVendorReferrer && (
          <Grid item xs={12} md={6}>
            <Controller
              name="vendorReferrers"
              defaultValue={[]}
              control={control}
              render={({ field }) => (
                <ReferrarAutoComplete
                  id="vendorReferrers"
                  referrerType={ReferrerTypeEnumModel.Vendor}
                  helperText={undefined}
                  error={undefined}
                  selected={field.value ?? []}
                  onChange={(event, value: ILookupItemSimpleModel[] | null) => {
                    field.onChange(value);
                  }}
                  label={"Vendor Referrer"}
                ></ReferrarAutoComplete>
              )}
            />
          </Grid>
        )}
      {selectedSource &&
        !hiddenFields.find((x) => x.name === "source") &&
        userData?.sources?.find((x) => x.id === selectedSource)
          ?.showSoiReferrer && (
          <Grid item xs={12} md={6}>
            <Controller
              name="soiReferrers"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <ReferrarAutoComplete
                  id="soiReferrers"
                  referrerType={ReferrerTypeEnumModel.Soi}
                  helperText={undefined}
                  error={undefined}
                  selected={field.value ?? []}
                  onChange={(event, value: ILookupItemSimpleModel[] | null) => {
                    field.onChange(value);
                  }}
                  label={"SOI Referrer"}
                ></ReferrarAutoComplete>
              )}
            />
          </Grid>
        )}
      {selectedSource &&
        !hiddenFields.find((x) => x.name === "source") &&
        userData?.sources?.find((x) => x.id === selectedSource)
          ?.showAgentReferrer && (
          <Grid item xs={12} md={6}>
            <Controller
              name="agentReferrers"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <ReferrarAutoComplete
                  id="agentReferrers"
                  referrerType={ReferrerTypeEnumModel.Agent}
                  helperText={undefined}
                  error={undefined}
                  selected={field.value ?? []}
                  onChange={(event, value: ILookupItemSimpleModel[] | null) => {
                    field.onChange(value);
                  }}
                  label={"Agent Referrer"}
                ></ReferrarAutoComplete>
              )}
            />
          </Grid>
        )}

      {showReferralOut && (
        <Grid item xs={12} md={6}>
          <Controller
            name="agentReferrered"
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <ReferrarAutoComplete
                id="agentReferrered"
                referrerType={ReferrerTypeEnumModel.ReferredOut}
                helperText={undefined}
                error={undefined}
                selected={field.value ?? []}
                onChange={(event, value: ILookupItemSimpleModel[] | null) => {
                  field.onChange(value);
                }}
                label={"Agent Referred"}
              ></ReferrarAutoComplete>
            )}
          />
        </Grid>
      )}

      {showReferralOut && (
        <Grid item xs={12} md={6}>
          <Controller
            name="referralOutTypeId"
            control={control}
            defaultValue={""}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel shrink={true}>Referral Out Type</InputLabel>
                <Select
                  displayEmpty
                  {...field}
                  notched={true}
                  readOnly={readonly}
                  label="Referral Out Type"
                  value={field.value ?? ""}
                  onChange={(e) => {
                    field.onChange(e.target.value ?? null);
                  }}
                  endAdornment={
                    field.value &&
                    !readonly && (
                      <IconButton
                        size="small"
                        sx={{ mr: 2 }}
                        onClick={(e) => {
                          field.onChange(null);
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    )
                  }
                >
                  {userData?.referralOutTypes
                    ?.filter((x) => x.name !== undefined && x.name !== "")
                    ?.sort((a, b) => a.name!.localeCompare(b.name!))
                    ?.filter((x) => x.deleted !== true)
                    ?.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
      )}

      <Grid item xs={12} md={6}>
        <Controller
          name="clients"
          control={control}
          defaultValue={[]}
          rules={{
            // Required for Lead/Active Buyer, Tenant and Referral Out entries
            validate: (value: ILookupItemModel[] | null) =>
              !!value?.length ||
              !isLeadOrActive(statusRef.current) ||
              !currentLeadRequirements().clients ||
              "Please add a client",
          }}
          render={({ field, fieldState: { error } }) => (
            <ClientAutoComplete
              helperText={error?.message}
              error={!!error}
              onChange={(event, value: ILookupItemModel[] | null) => {
                field.onChange(value);
              }}
              id="clients"
              selected={field.value ?? []}
              label={clientsRequired ? "Clients *" : "Clients"}
              placeholder="Search or add new clients"
            ></ClientAutoComplete>
          )}
        />
      </Grid>

      {hiddenFields?.find((x) => x.name === "reason") === undefined && (
        <Grid item xs={12} md={6}>
          <Controller
            name="reason"
            control={control}
            defaultValue={""}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel shrink={true}>Reason</InputLabel>
                <Select
                  displayEmpty
                  {...field}
                  notched={true}
                  readOnly={readonly}
                  label="Reason"
                  value={field.value ?? ""}
                  onChange={(e) => {
                    // const selected = reasonOptions.find(
                    //   (opt) => opt.id === e.target.value
                    // );
                    field.onChange(e.target.value ?? null);
                  }}
                  endAdornment={
                    field.value &&
                    !readonly && (
                      <IconButton
                        size="small"
                        sx={{ mr: 2 }}
                        onClick={(e) => {
                          field.onChange(null);
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    )
                  }
                >
                  {userData?.reasons
                    ?.filter((x) => x.name !== undefined || x.name !== "")
                    ?.sort((a, b) => a.name!.localeCompare(b.name!))
                    ?.filter(
                      (x) =>
                        (isCreate && x.deleted !== true) ||
                        (isCreate === false &&
                          (selectedReason === x.id || x.deleted !== true)),
                    )
                    ?.map((reason) => (
                      <MenuItem key={reason.id} value={reason.id}>
                        {reason.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
      )}

      {!hiddenFields.find((x) => x.name === "customArea") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="area"
            defaultValue={""}
            control={control}
            render={({ field }) => (
              <SingleValueAutoCompleteCategory
                readOnly={readonly}
                freeSolo={true}
                id="area"
                options={userData?.areas ?? []}
                value={field.value ?? null}
                onChange={(event, value: ILookupItemModel | null) => {
                  field.onChange(value);
                }}
                helperText={undefined}
                placeholder="Select"
                error={undefined}
                label={"Custom Area"}
              />
            )}
          />
        </Grid>
      )}

      {!hiddenFields.find((x) => x.name === "coAgent") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="coAgentMember"
            defaultValue={null}
            control={control}
            render={({ field }) => (
              <SingleValueAutoCompleteCategory
                readOnly={readonly}
                id="coAgentMember"
                options={userData?.coAgents ?? []}
                value={field.value ?? null}
                onChange={(event, value: ILookupItemModel | null) => {
                  field.onChange(value);
                }}
                helperText={undefined}
                error={undefined}
                label={"Team Member/Co-Agent"}
                placeholder={"Type or Select"}
              />
            )}
          />
        </Grid>
      )}

      {!hiddenFields.find((x) => x.name === "coAgent") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="coAgentMember2"
            defaultValue={null}
            control={control}
            render={({ field }) => (
              <SingleValueAutoCompleteCategory
                readOnly={readonly}
                id="coAgentMember2"
                options={userData?.coAgents ?? []}
                value={field.value ?? null}
                onChange={(event, value: ILookupItemModel | null) => {
                  field.onChange(value);
                }}
                helperText={undefined}
                error={undefined}
                label={"Team Member/Co-Agent 2"}
                placeholder={"Type or Select"}
              />
            )}
          />
        </Grid>
      )}

      {!hiddenFields.find((x) => x.name === "specialGroups") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="specialGroups"
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <MultiValueAutoCompleteCategory
                readOnly={readonly}
                options={userData?.specialGroups ?? []}
                id="specialGroups"
                freeSolo={false}
                value={field.value}
                onChange={(event, value: ILookupItemModel[] | null) => {
                  field.onChange(value);
                }}
                helperText={undefined}
                error={undefined}
                label={"Special Groups"}
                placeholder={"Select"}
              />
            )}
          />
        </Grid>
      )}
      {!hiddenFields.find((x) => x.name === "specialTags") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="specialTags"
            defaultValue={[]}
            control={control}
            render={({ field }) => (
              <MultiValueAutoCompleteCategory
                options={userData?.specialTags ?? []}
                readOnly={readonly}
                id="specialTags"
                value={field.value ?? null}
                onChange={(event, value: ILookupItemModel[] | null) => {
                  field.onChange(value);
                }}
                helperText={undefined}
                error={undefined}
                label={"Special Tags"}
                freeSolo={false}
                placeholder={"Select"}
              />
            )}
          />
        </Grid>
      )}
      {!hiddenFields.find((x) => x.name === "vendors") && (
        <Grid item xs={12} md={6}>
          <Controller
            name="vendors"
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <ClientAutoComplete
                helperText={undefined}
                error={undefined}
                onChange={(event, value: ILookupItemSimpleModel[] | null) => {
                  field.onChange(value);
                }}
                id="vendors"
                selected={field.value ?? []}
                label={"Vendors"}
                placeholder="Search or add new vendors"
              />
            )}
          />
        </Grid>
      )}
      {!hiddenFields.find((x) => x.name === "notes") && (
        <Grid item xs={12}>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField
                disabled={readonly}
                {...field}
                label="Notes"
                value={field.value ?? ""}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                  input: {
                    notched: true,
                  },
                }}
                multiline
                rows={4}
                fullWidth
              />
            )}
          />
        </Grid>
      )}
      {/* <Grid item xs={12}>
        <CustomAutocomplete options={mockTags.map((s) => s.name)} />
      </Grid> */}
      <Dialog open={pendingStatus !== null} onClose={() => setPendingStatus(null)}>
        <DialogTitle>
          {isMovingToTransactions ? "Move to Closed?" : "Move to Pipeline?"}
        </DialogTitle>
        <DialogContent>
          {isMovingToTransactions ? (
            <DialogContentText>
              Changing the status to Closed moves this entry from the Pipeline
              folder to{" "}
              {closingDate
                ? `the ${dayjs(closingDate).year()} folder (the year of its Closing Date)`
                : "the year folder of its Closing Date"}{" "}
              once saved. It will count in that year's summaries and analytics,
              and your brokerage anniversary financial defaults will be applied.
            </DialogContentText>
          ) : (
            <DialogContentText>
              Changing the status to{" "}
              {TRANSACTION_STATUS_OPTIONS.find((o) => o.value === pendingStatus)
                ?.label ?? ""}{" "}
              moves this entry to the Pipeline folder once saved. It will no
              longer count in its year folder, summaries or analytics until its
              status is set back to Closed.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingStatus(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingStatus !== null) applyStatus(pendingStatus);
              setPendingStatus(null);
            }}
          >
            {isMovingToTransactions ? "Move to Closed" : "Move to Pipeline"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
